import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Radio, Smartphone, Video, Gavel, Package } from 'lucide-react';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { AuthPromptModal } from '../components/AuthPromptModal';

// Landing page color palette (scoped, not global) — light theme
const lp = {
  midnightNavy: '#1C2033',
  deepSlate: '#252A42',
  iconPurple: 'var(--color-primary, #5F3DC4)',
  midViolet: 'var(--color-primary-mid, #9781D9)',
  softLavender: 'var(--color-primary-light, #BCAEE6)',
  offWhite: '#F4F3F8',
  mist: '#E8E6F0',
  mutedSlate: '#6B6F85',
  white: '#FFFFFF',
  ctaGradient: 'linear-gradient(135deg, var(--color-primary, #5F3DC4), var(--color-primary-light, #BCAEE6))',
};

// Inject DM Sans from Google Fonts (BW Modelica is loaded globally in index.css)
const dmSansLinkId = 'landing-page-dm-sans';

function injectFonts() {
  if (document.getElementById(dmSansLinkId)) return;
  const link = document.createElement('link');
  link.id = dmSansLinkId;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

function cleanupFonts() {
  document.getElementById(dmSansLinkId)?.remove();
}

// CSS phone mockup component
function PhoneMockup() {
  return (
    <div className="lp-phone-wrapper" style={phonStyles.wrapper}>
      <div style={phonStyles.phone}>
        <div style={phonStyles.notch} />
        <div style={phonStyles.screen}>
          {/* Fake app header */}
          <div style={phonStyles.appHeader}>
            <img src="/img/snagg_assets/snagg_combined_color.svg" alt="Snagg" style={phonStyles.appLogoImg} />
            <div style={phonStyles.liveBadge}>LIVE</div>
          </div>
          {/* Fake video area */}
          <div style={phonStyles.videoArea}>
            <div style={phonStyles.videoPlaceholder}>
              <Radio size={32} color={lp.softLavender} />
            </div>
            <div style={phonStyles.viewerCount}>
              <div style={phonStyles.viewerDot} />
              <span>243 watching</span>
            </div>
          </div>
          {/* Fake chat bubbles */}
          <div style={phonStyles.chatArea}>
            <div style={phonStyles.chatBubble}>
              <span style={phonStyles.chatUser}>@buyer1</span> Bid $45!
            </div>
            <div style={phonStyles.chatBubble}>
              <span style={phonStyles.chatUser}>@sneakfan</span> Love this drop
            </div>
            <div style={phonStyles.chatBubble}>
              <span style={phonStyles.chatUser}>@vintage99</span> $52!
            </div>
          </div>
          {/* Fake bid bar */}
          <div style={phonStyles.bidBar}>
            <div style={phonStyles.bidInput}>Place bid...</div>
            <div style={phonStyles.bidButton}>BID</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const RESPONSIVE_CSS = `
  @media (max-width: 768px) {
    .lp-hero-inner {
      flex-direction: column !important;
      gap: 40px !important;
      padding-top: 100px !important;
      padding-bottom: 60px !important;
      text-align: center;
    }
    .lp-hero-headline {
      font-size: 36px !important;
    }
    .lp-hero-sub {
      max-width: 100% !important;
      font-size: 18px !important;
    }
    .lp-hero-right {
      flex-shrink: 1 !important;
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .lp-phone-wrapper {
      transform: scale(0.85);
      transform-origin: top center;
    }
    .lp-store-badges {
      justify-content: center;
    }
    .lp-steps-grid {
      grid-template-columns: 1fr !important;
      gap: 16px !important;
    }
    .lp-section {
      padding: 56px 0 !important;
    }
    .lp-section-title {
      font-size: 28px !important;
      margin-bottom: 24px !important;
    }
    .lp-section-intro {
      font-size: 16px !important;
      margin-bottom: 36px !important;
    }
  }
  @media (max-width: 480px) {
    .lp-hero-headline {
      font-size: 30px !important;
    }
    .lp-phone-wrapper {
      transform: scale(0.75);
    }
  }
`;

export function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  useDocumentTitle();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forceSignup = params.get('signup') === 'true';
    if (forceSignup) {
      setSignupModalOpen(true);
      return;
    }
    if (!isLoading && isAuthenticated && params.get('landing') !== 'true') {
      navigate('/shows', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    injectFonts();
    return () => cleanupFonts();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div style={styles.page}>
      <style>{RESPONSIVE_CSS}</style>
      {/* Nav Bar */}
      <nav style={{
        ...styles.nav,
        backgroundColor: scrolled ? lp.white : 'transparent',
        borderBottom: scrolled ? `1px solid ${lp.mist}` : 'none',
        boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.06)' : 'none',
      }}>
        <div style={styles.navContainer}>
          <Link to="/" style={styles.navLogoLink}>
            <img src="/img/snagg_assets/snagg_combined_color.svg" alt="snagg" style={styles.navLogoImg} />
          </Link>
          {/* TODO: restore nav actions when public show browsing is ready */}
        </div>
      </nav>

      {/* Hero — full viewport height, split layout */}
      <section style={styles.hero}>
        <div className="lp-hero-inner" style={styles.heroInner}>
          <div style={styles.heroLeft}>
            <span style={styles.comingSoonBadge}>Coming Soon</span>
            <h1 className="lp-hero-headline" style={styles.heroHeadline}>see it. want it. <span style={{
              fontFamily: "'SnaggBrand', sans-serif",
              letterSpacing: '0.04em',
              background: `linear-gradient(135deg, var(--color-primary, #5F3DC4), var(--color-primary-light, #BCAEE6), var(--color-primary-rose, #CD3C9C))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
            }}>snagg</span> it.</h1>
            <p className="lp-hero-sub" style={styles.heroSub}>
              Live auctions are coming. Watch sellers go live, bid in real time, and win.
            </p>
            <div className="lp-store-badges" style={styles.storeBadges}>
              <a href="#" style={styles.storeBadge} onClick={(e) => e.preventDefault()}>
                <Smartphone size={20} color={lp.iconPurple} />
                <div>
                  <div style={styles.storeBadgeSmall}>Coming Soon on</div>
                  <div style={styles.storeBadgeLabel}>App Store</div>
                </div>
              </a>
              <a href="#" style={styles.storeBadge} onClick={(e) => e.preventDefault()}>
                <Smartphone size={20} color={lp.iconPurple} />
                <div>
                  <div style={styles.storeBadgeSmall}>Coming Soon on</div>
                  <div style={styles.storeBadgeLabel}>Google Play</div>
                </div>
              </a>
            </div>
          </div>
          <div className="lp-hero-right" style={styles.heroRight}>
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* What is Snagg */}
      <section className="lp-section" style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 className="lp-section-title" style={styles.sectionTitle}>
            Shopping, <span style={styles.sectionTitleAccent}>live</span>. Not listed.
          </h2>
          <p className="lp-section-intro" style={styles.sectionIntro}>
            Sellers go live. You watch, chat, bid, and win — all in real time.
          </p>
          <div className="lp-category-pills" style={styles.categoryPills}>
            <span style={styles.categoryPill}>Sneakers</span>
            <span style={styles.categoryPill}>Trading Cards</span>
            <span style={styles.categoryPill}>Vintage</span>
            <span style={styles.categoryPill}>Collectibles</span>
          </div>
          <p style={styles.betaCaption}>US-based. Currently in private beta.</p>
          <div className="lp-steps-grid" style={styles.stepsGrid}>
            <div style={styles.stepCard}>
              <div style={styles.stepIcon}><Video size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.stepTitle}>Watch live</h3>
              <p style={styles.stepDesc}>
                Tune in to HD shows from sellers dropping new inventory every day.
              </p>
            </div>
            <div style={styles.stepCard}>
              <div style={styles.stepIcon}><Gavel size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.stepTitle}>Bid in real time</h3>
              <p style={styles.stepDesc}>
                Chat with the host and snipe the win before anyone else does.
              </p>
            </div>
            <div style={styles.stepCard}>
              <div style={styles.stepIcon}><Package size={20} color="#fff" strokeWidth={2.25} /></div>
              <h3 style={styles.stepTitle}>Ships fast</h3>
              <p style={styles.stepDesc}>
                Winning bids auto-checkout with your saved payment and ship with tracking.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <AuthPromptModal
        isOpen={signupModalOpen}
        onClose={() => setSignupModalOpen(false)}
        onAuthenticated={() => {
          setSignupModalOpen(false);
          navigate('/shows', { replace: true });
        }}
        initialTab="signup"
        startAtCredentialForm
      />
    </div>
  );
}

// Phone mockup styles
const phonStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  phone: {
    width: 280,
    height: 560,
    backgroundColor: lp.midnightNavy,
    borderRadius: 40,
    padding: 12,
    boxShadow: '0 24px 64px rgba(28, 32, 51, 0.25)',
    position: 'relative',
  },
  notch: {
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 100,
    height: 28,
    backgroundColor: lp.midnightNavy,
    borderRadius: '0 0 16px 16px',
    zIndex: 2,
  },
  screen: {
    width: '100%',
    height: '100%',
    backgroundColor: lp.white,
    borderRadius: 28,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  appHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '36px 16px 10px',
    borderBottom: '1px solid #E8E6F0',
  },
  appLogoImg: {
    height: 20,
    width: 'auto',
  },
  liveBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: '#DC2626',
    padding: '3px 8px',
    borderRadius: 4,
    letterSpacing: 1,
  },
  videoArea: {
    flex: 1,
    margin: '10px 10px 0',
    borderRadius: 12,
    backgroundColor: lp.offWhite,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: 180,
  },
  videoPlaceholder: {
    opacity: 0.4,
  },
  viewerCount: {
    position: 'absolute',
    top: 8,
    right: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    color: lp.mutedSlate,
    backgroundColor: 'rgba(0,0,0,0.06)',
    padding: '3px 8px',
    borderRadius: 10,
  },
  viewerDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#DC2626',
  },
  chatArea: {
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  chatBubble: {
    fontSize: 11,
    color: lp.midnightNavy,
    backgroundColor: lp.offWhite,
    padding: '5px 10px',
    borderRadius: 8,
  },
  chatUser: {
    fontWeight: 600,
    color: lp.iconPurple,
  },
  bidBar: {
    display: 'flex',
    gap: 6,
    padding: '8px 10px 14px',
  },
  bidInput: {
    flex: 1,
    fontSize: 12,
    color: lp.mutedSlate,
    backgroundColor: lp.offWhite,
    padding: '8px 12px',
    borderRadius: 8,
  },
  bidButton: {
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: lp.ctaGradient,
    padding: '8px 16px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
  },
};

// Page styles
const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'DM Sans', system-ui, sans-serif",
    color: lp.midnightNavy,
    backgroundColor: lp.white,
    minHeight: '100vh',
    overflowX: 'hidden',
  },

  // Nav
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    transition: 'all 0.3s',
  },
  navContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navLogoLink: {
    textDecoration: 'none',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  navLogoImg: {
    height: 32,
    width: 'auto',
    display: 'block',
  },
  navActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  navCta: {
    padding: '8px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    background: lp.ctaGradient,
    textDecoration: 'none',
    borderRadius: 8,
    border: 'none',
    transition: 'opacity 0.2s',
  },

  // Hero
  hero: {
    backgroundColor: lp.offWhite,
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
  },
  heroInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 64,
    width: '100%',
  },
  heroLeft: {
    flex: 1,
  },
  heroRight: {
    flexShrink: 0,
  },
  comingSoonBadge: {
    display: 'inline-block',
    padding: '6px 20px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    background: lp.ctaGradient,
    borderRadius: 100,
    marginBottom: 24,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  heroHeadline: {
    fontFamily: "'BW Modelica', system-ui, sans-serif",
    fontSize: 56,
    fontWeight: 800,
    lineHeight: 1.1,
    marginBottom: 20,
    color: lp.midnightNavy,
  },
  heroSub: {
    fontSize: 20,
    lineHeight: 1.6,
    color: lp.mutedSlate,
    maxWidth: 480,
    marginBottom: 40,
  },
  storeBadges: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  storeBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 24px',
    backgroundColor: lp.white,
    border: `1px solid ${lp.mist}`,
    borderRadius: 12,
    textDecoration: 'none',
    color: lp.midnightNavy,
    transition: 'background-color 0.2s',
  },
  storeBadgeSmall: {
    fontSize: 11,
    color: lp.mutedSlate,
    lineHeight: 1.2,
  },
  storeBadgeLabel: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
  },

  // Sections
  section: {
    backgroundColor: lp.white,
    padding: '80px 0',
  },
  sectionInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
  },
  sectionTitle: {
    fontFamily: "'BW Modelica', system-ui, sans-serif",
    fontSize: 36,
    fontWeight: 800,
    textAlign: 'center',
    marginBottom: 24,
    color: lp.midnightNavy,
  },
  sectionTitleAccent: {
    background: `linear-gradient(135deg, var(--color-primary, #5F3DC4), var(--color-primary-light, #BCAEE6), var(--color-primary-rose, #CD3C9C))`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
  },
  sectionIntro: {
    fontSize: 18,
    lineHeight: 1.6,
    color: lp.mutedSlate,
    textAlign: 'center',
    maxWidth: 720,
    margin: '0 auto 20px',
  },

  // How It Works
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  stepCard: {
    backgroundColor: lp.offWhite,
    borderRadius: 16,
    padding: 32,
    textAlign: 'center',
    border: `1px solid ${lp.mist}`,
  },
  stepIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: 12,
    background: lp.ctaGradient,
    marginBottom: 16,
  },
  categoryPills: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 16,
  },
  categoryPill: {
    display: 'inline-block',
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 600,
    color: lp.iconPurple,
    backgroundColor: lp.offWhite,
    border: `1px solid ${lp.mist}`,
    borderRadius: 100,
  },
  betaCaption: {
    fontSize: 13,
    color: lp.mutedSlate,
    textAlign: 'center',
    marginBottom: 48,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 8,
    color: lp.midnightNavy,
  },
  stepDesc: {
    fontSize: 15,
    lineHeight: 1.6,
    color: lp.mutedSlate,
  },

};
